import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, RefreshCcw, Loader2 } from "lucide-react";
import { Detail } from "@/pages/site/SiteWorkspacePage";
import PhaseHistoryPanel from "@/components/execution/PhaseHistoryPanel";

/**
 * Full-screen detail page for a single execution project.
 *
 * Routed at `/my-projects/:pid`. Reuses the `Detail` component already used by
 * the desktop split-view in `SiteWorkspacePage.jsx`, so all Site Initiation /
 * Electrical Meeting / Execution Stages / Resource-Team behaviour stays
 * identical — the only difference is this page has no left-side project
 * sidebar (mobile-first, full-screen).
 *
 * When the user hits Back, `MyProjectsPage` restores the previous scroll +
 * filter via `sessionStorage`.
 */
export default function MyProjectDetailPage() {
  const { pid } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin, roleName, roleType, principal } = useAuth();
  const isExecMgr = isAdmin || roleName === "Execution Manager" || roleType === "director";
  const isPm = roleName === "Project Manager";
  const isSup = roleName === "Site Supervisor";
  const can95 = isAdmin || roleType === "director" || ["Finance Manager", "CRM Manager", "CRM Executive"].includes(roleName);

  // Deep-link params (from Pending Actions / notification clicks)
  const focusStage = search.get("stage");
  const focusDispatch = search.get("focus_dispatch");
  const focusBulk = search.get("focus_bulk");
  const initialTab = search.get("tab");
  const focusSnag = search.get("focus_snag");

  const [sel, setSel] = useState(null);
  const [pms, setPms] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);

  const loadDetail = useCallback(async (silent = false) => {
    if (!pid) return;
    if (silent) setReloading(true); else setLoading(true);
    try {
      const [p, pmRes, supRes] = await Promise.all([
        api.get(`/execution/projects/${pid}`),
        isExecMgr ? api.get("/users/by-role/Project Manager").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        isExecMgr ? api.get("/users/by-role/Site Supervisor").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setSel(p.data);
      setPms(pmRes.data || []);
      setSupervisors(supRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); setReloading(false); }
  }, [pid, isExecMgr]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const goBack = () => {
    // Prefer history.back so scroll + filter restoration from MyProjectsPage
    // kicks in. Fallback: navigate to list if no history.
    if (window.history.length > 1) navigate(-1);
    else navigate("/my-projects");
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="my-project-detail-page">
      {/* Sticky top bar with Back — fills the full mobile width */}
      <div className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur-sm border-b border-[#262626]">
        <div className="px-4 sm:px-10 py-3 flex items-center gap-3">
          <button onClick={goBack} className="iz-btn-secondary text-xs flex items-center gap-1.5"
                  data-testid="my-project-back">
            <ArrowLeft className="w-3.5 h-3.5"/> Back
          </button>
          <div className="flex-1 min-w-0">
            {sel && (
              <>
                <p className="text-white text-sm truncate">{sel.client_name}{sel.city ? ` · ${sel.city}` : ""}</p>
                <p className="text-[11px] text-gray-500 truncate">{sel.project_name}</p>
                {sel.my_project_stage_label && (
                  <span className="inline-block mt-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5"
                        data-testid="my-project-phase-pill">
                    Phase · {sel.my_project_stage_label}
                  </span>
                )}
              </>
            )}
          </div>
          <button onClick={() => loadDetail(true)} disabled={reloading || loading}
                  className="iz-btn-secondary text-xs flex items-center gap-1"
                  data-testid="my-project-refresh">
            <RefreshCcw className={`w-3.5 h-3.5 ${reloading ? "animate-spin" : ""}`}/>
          </button>
        </div>
      </div>

      <div className="px-4 sm:px-10 py-5">
        {loading && !sel && (
          <div className="iz-card p-12 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin"/> Loading project…
          </div>
        )}
        {!loading && !sel && (
          <div className="iz-card p-12 text-center text-sm text-gray-500">Project not found or access denied.</div>
        )}
        {sel && (
          <>
            <PhaseHistoryPanel
              projectId={sel.project_id || sel.id}
              currentPhase={sel.my_project_stage}
              currentPhaseLabel={sel.my_project_stage_label}
            />
            <Detail
              sel={sel}
              pms={pms}
              supervisors={supervisors}
              isExecMgr={isExecMgr}
              isPm={isPm}
              isSup={isSup}
              can95={can95}
              principalId={principal?.id}
              roleName={roleName}
              onRefresh={() => loadDetail(true)}
              focusStage={focusStage}
              focusDispatch={focusDispatch}
              focusBulk={focusBulk}
              initialTab={initialTab}
              focusSnag={focusSnag}
            />
          </>
        )}
      </div>
    </div>
  );
}
